import { getAnonymousId } from '@/utils/anonymousId';

/**
 * Shape persisted to localStorage. One blob per viewer:
 *   ccms:progress:<owner> = { [sessionId]: SessionProgress }
 *
 * "owner" is either `anon:<anonymousId>` (visitors) or the authenticated
 * userId. On sign-in we copy the anon blob into the user blob so the viewer
 * keeps their progress.
 *
 * Keeping the whole map under one key (vs. one key per session) makes
 * course-wide reads cheap and lets us migrate on sign-in with a single
 * read+write instead of scanning localStorage.
 */
export interface SessionProgress {
  position: number;       // last reported playback time (s)
  duration: number;       // best-effort duration (s)
  status: SessionStatus;  // 'not-started' | 'in-progress' | 'completed'
  completedAt?: number;   // ms epoch
  lastAt: number;         // ms epoch — when this session was last touched
}

export type SessionStatus = 'not-started' | 'in-progress' | 'completed';

export const NEAR_END_THRESHOLD = 5;

export function ownerKey(userId: string | null | undefined): string {
  return userId && userId.length > 0 ? userId : `anon:${getAnonymousId()}`;
}

function rootKey(owner: string): string {
  return `ccms:progress:${owner}`;
}

export function readProgress(userId: string | null | undefined): Record<string, SessionProgress> {
  try {
    const raw = window.localStorage.getItem(rootKey(ownerKey(userId)));
    if (!raw) return {};
    return JSON.parse(raw) as Record<string, SessionProgress>;
  } catch {
    return {};
  }
}

export function readSessionProgress(
  userId: string | null | undefined,
  sessionId: number,
): SessionProgress | null {
  const all = readProgress(userId);
  return all[`s${sessionId}`] ?? null;
}

export function writeSessionProgress(
  userId: string | null | undefined,
  sessionId: number,
  patch: Partial<SessionProgress>,
): SessionProgress {
  const all = readProgress(userId);
  const key = `s${sessionId}`;
  const prev = all[key] ?? {
    position: 0,
    duration: 0,
    status: 'not-started' as SessionStatus,
    lastAt: 0,
  };
  const next: SessionProgress = {
    ...prev,
    ...patch,
    lastAt: Date.now(),
  };
  all[key] = next;
  try {
    window.localStorage.setItem(rootKey(ownerKey(userId)), JSON.stringify(all));
    notifyProgressChanged();
  } catch {
    /* quota / private mode — silently ignore */
  }
  return next;
}

export function clearSessionProgress(userId: string | null | undefined, sessionId: number): void {
  const all = readProgress(userId);
  const key = `s${sessionId}`;
  if (!(key in all)) return;
  delete all[key];
  try {
    window.localStorage.setItem(rootKey(ownerKey(userId)), JSON.stringify(all));
    notifyProgressChanged();
  } catch {
    /* ignore */
  }
}

function notifyProgressChanged(): void {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(new CustomEvent('ccms:progress-changed'));
  } catch {
    /* ignore */
  }
}

/**
 * Move a viewer's anonymous progress blob into their user-keyed blob on
 * sign-in. Existing user-keyed entries take precedence (we never overwrite
 * progress from a previous device with stale anon data).
 *
 * Also migrates per-session notes (separate localStorage namespace).
 */
export function migrateAnonymousProgressTo(userId: string): { migrated: number; notesMigrated: number } {
  const anon = `anon:${getAnonymousId()}`;
  if (!userId || userId === anon) return { migrated: 0, notesMigrated: 0 };

  let migrated = 0;
  try {
    const anonRaw = window.localStorage.getItem(rootKey(anon));
    if (anonRaw) {
      const anonMap = JSON.parse(anonRaw) as Record<string, SessionProgress>;
      const userMap = readProgress(userId);
      for (const [k, v] of Object.entries(anonMap)) {
        const existing = userMap[k];
        if (!existing || existing.lastAt < (v.lastAt ?? 0)) {
          userMap[k] = v;
          migrated += 1;
        }
      }
      window.localStorage.setItem(rootKey(userId), JSON.stringify(userMap));
      window.localStorage.removeItem(rootKey(anon));
    }
  } catch {
    /* ignore */
  }

  // Notes namespace lives under `ccms:notes:<owner>:s<id>` — scan + move.
  let notesMigrated = 0;
  try {
    const anonNotePrefix = `ccms:notes:${anon}:`;
    const userNotePrefix = `ccms:notes:${userId}:`;
    const keys: string[] = [];
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const k = window.localStorage.key(i);
      if (k && k.startsWith(anonNotePrefix)) keys.push(k);
    }
    for (const oldKey of keys) {
      const value = window.localStorage.getItem(oldKey);
      if (value == null) continue;
      const newKey = userNotePrefix + oldKey.slice(anonNotePrefix.length);
      // Don't overwrite a note already saved under the user key.
      if (window.localStorage.getItem(newKey) == null) {
        window.localStorage.setItem(newKey, value);
        notesMigrated += 1;
      }
      window.localStorage.removeItem(oldKey);
    }
  } catch {
    /* ignore */
  }

  return { migrated, notesMigrated };
}

/**
 * Aggregate stats for a set of session ids — used by the course detail page.
 */
export function summariseCourseProgress(
  userId: string | null | undefined,
  sessionIds: number[],
): {
  total: number;
  completed: number;
  inProgress: number;
  notStarted: number;
  percentComplete: number;
  lastTouchedSessionId: number | null;
  perSession: Record<number, SessionProgress | null>;
} {
  const all = readProgress(userId);
  const perSession: Record<number, SessionProgress | null> = {};
  let completed = 0;
  let inProgress = 0;
  let lastTouched: { id: number; at: number } | null = null;

  for (const id of sessionIds) {
    const entry = all[`s${id}`] ?? null;
    perSession[id] = entry;
    if (!entry) continue;
    if (entry.status === 'completed') completed += 1;
    else if (entry.status === 'in-progress') inProgress += 1;
    if (!lastTouched || entry.lastAt > lastTouched.at) {
      lastTouched = { id, at: entry.lastAt };
    }
  }

  const total = sessionIds.length;
  return {
    total,
    completed,
    inProgress,
    notStarted: Math.max(0, total - completed - inProgress),
    percentComplete: total > 0 ? Math.round((completed / total) * 100) : 0,
    lastTouchedSessionId: lastTouched?.id ?? null,
    perSession,
  };
}
